<template>
    <PageSection>
        <PageHeader title="Adoptions">
            <router-link to="/catalog-search" class="btn-primary my-1">
                Choose Content
            </router-link>
        </PageHeader>
        <transition name="fade" mode="out-in">
            <div v-if="isLoading || !hasProfessorRows"
                class="grow flex items-center justify-center card border border-purple-100">
                <transition name="fade" mode="out-in">
                    <LoadingSpinner v-if="isLoading" ariaLabel="Page loading" />
                    <EmptyState v-else-if="!hasProfessorRows" :title="message.title" :text="message.description"
                        :icon="message.icon" variant="plain" class="mx-4" />
                </transition>
            </div>
            <div v-else class="gap-y-8">
                <div v-for="(courseItems, academicPath) in professorRows" class="gap-y-8">
                    <div v-for="(items, courseKey) in courseItems" class="gap-y-4">
                        <h2 class="font-bold font-secondary">
                            {{ courseKey }}
                            <span> : </span>
                            {{ items.course.item.course_name }}
                        </h2>
                        <AdoptionsTable v-if="!!items.booklist.length" :booklist="items.booklist" :columns="columns"
                            :columnPinning="columnPinning" :isLoading="isLoading"
                            :hasApprovalPermission="hasApprovalPermission" />
                        <EmptyState v-else title="No content found" :icon="SearchFailIcon" />
                    </div>
                </div>
            </div>
        </transition>
    </PageSection>
</template>

<script lang="ts" setup>
import { type BooklistRowConfirmed } from "core-services/stacks/institutions/substacks/booklist/lib/schemas/booklist-row-confirmed";
import { watch } from "vue";
import { definePage } from "vue-router/auto";
import { useGuardedTableColumns } from "../../../../shared/components/ComposableTable/useGuardedTableColumns";
import EmptyState from "../../../../shared/components/EmptyState.vue";
import LoadingSpinner from "../../../../shared/components/LoadingSpinner.vue";
import PageSection from "../../../../shared/components/PageSection.vue";
import SearchFailIcon from "../../assets/icons/search-fail.svg?component";
import AdoptionsTable from "../../components/Adoptions/AdoptionsTable.vue";
import PageHeader from "../../components/PageHeader.vue";
import { usePermission } from "../../composables/usePermission";
import { useProfessorViewTable } from "../../composables/useProfessorViewTable";
import { parseToDateOrUndefined } from "../../config";
import { useAcademicDates } from "../../stores/academic-dates";
import { useInstitution } from "../../stores/institution";
import { generateTableColumns } from "../../utils/booklist-table-columns";

const institution = useInstitution();

const { hasPermission, getUserPermissions } = usePermission();

const academicDates = useAcademicDates();

const {
	professorRows,
	isLoading,
	hasProfessorRows,
	message,
	columns: booklistColumns,
	columnPinning,
	fetchProfessorRows,
} = useProfessorViewTable({
	startDate: parseToDateOrUndefined(academicDates.selectedDates?.startDate),
	endDate: parseToDateOrUndefined(academicDates.selectedDates?.endDate),
});

// TODO: This can all be handled by the state machine.
watch(academicDates, () => {
	fetchProfessorRows({
		startDate: parseToDateOrUndefined(academicDates?.selectedDates?.startDate),
		endDate: parseToDateOrUndefined(academicDates?.selectedDates?.endDate),
	});
});
const userPermissions = await getUserPermissions();

const hasApprovalPermission = computed(
	() =>
		institution.institutionConfig?.approval_config === "adoption_approvals" &&
		hasPermission(userPermissions, "booklist:approve", true),
);

// The columns we want to display in the table.
const columns = useGuardedTableColumns<BooklistRowConfirmed>((helper) => [
	...generateTableColumns(booklistColumns.value, helper),
]);

definePage({
	meta: {
		requiresAuth: true,
		permission: "adoptions:viewAssignedCourses",
	},
});
</script>
